import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Plus,
  Eye,
  Check,
  Receipt,
  Banknote,
} from 'lucide-react';
import { billingService } from '../../api/services/billing.service';
import {
  InvoiceDTO,
  CreateInvoiceRequest,
  InvoiceStatus,
  invoiceStatusLabels,
  formatMoney,
} from '../../types/billing';
import { SkeletonTable } from '../../components/Common/Skeleton';
import ConfirmDialog from '../../components/Common/ConfirmDialog';
import { useAuth } from 'hooks/useAuth';
import { canCloseBooks, isPayable, isUsageInvoice, istToday, addDays, formatYmd } from './closeBooksRules';
import { useLatestRequest, clampPage } from './latestRequest';
import IstDateRange from './IstDateRange';
import OrgFilterSelect from './OrgFilterSelect';
import InvoiceUsageModal from './InvoiceUsageModal';
import SettleInvoiceModal from './SettleInvoiceModal';

const Invoices: React.FC = () => {
  const [invoices, setInvoices] = useState<InvoiceDTO[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  const [statusFilter, setStatusFilter] = useState<InvoiceStatus | ''>('');
  const [orgFilter, setOrgFilter] = useState('');
  /** Invoice-date range, IST days; invoice dates ARE IST days, so they go on the wire as they are. */
  const [dates, setDates] = useState({ from: '', to: '' });
  /** Bumped to re-read the list with the current filters, after a write. */
  const [reloads, setReloads] = useState(0);
  const beginRequest = useLatestRequest();
  const [loadedOnce, setLoadedOnce] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState<InvoiceDTO | null>(null);
  const [finalizing, setFinalizing] = useState<InvoiceDTO | null>(null);
  const [finalizeBusy, setFinalizeBusy] = useState(false);
  const [showUsageModal, setShowUsageModal] = useState(false);
  const [settling, setSettling] = useState<InvoiceDTO | null>(null);
  const [notice, setNotice] = useState<{ ok: boolean; text: string } | null>(null);

  const { user, currentOrganization } = useAuth();
  const isBillingAdmin = canCloseBooks((currentOrganization as any)?.role || (user as any)?.role);

  const [formData, setFormData] = useState<CreateInvoiceRequest>({
    organizationId: '',
    invoiceDate: istToday(),
    dueDate: addDays(istToday(), 30),
    items: [],
  });

  useEffect(() => {
    const isLatest = beginRequest();
    setLoading(true);
    billingService
      .getInvoices({
        page,
        size: 20,
        status: statusFilter || undefined,
        organizationId: orgFilter || undefined,
        from: dates.from || undefined,
        to: dates.to || undefined,
      })
      .then((response) => {
        if (!isLatest()) return;
        setLoadedOnce(true);
        if (response.success && response.data) {
          setInvoices(response.data.content);
          setTotalPages(response.data.totalPages);
          setTotalElements(response.data.totalElements);
          const last = clampPage(page, response.data.totalPages);
          if (last !== page) setPage(last);
        }
        setLoading(false);
      });
  }, [page, statusFilter, orgFilter, dates, reloads, beginRequest]);

  const loadInvoices = () => setReloads((n) => n + 1);

  /** `?invoice=<id>` (e.g. a shortfall invoice just created) opens that invoice's details. */
  const [searchParams] = useSearchParams();
  const linkedInvoiceId = Number(searchParams.get('invoice'));
  useEffect(() => {
    if (!Number.isInteger(linkedInvoiceId) || linkedInvoiceId <= 0) return;
    let live = true;
    billingService.getInvoice(linkedInvoiceId).then((r) => {
      if (!live) return;
      if (r.success && r.data) {
        setSelectedInvoice(r.data);
        setShowDetailsModal(true);
      } else {
        setNotice({ ok: false, text: r.error || `Invoice #${linkedInvoiceId} could not be loaded` });
      }
    });
    return () => {
      live = false;
    };
  }, [linkedInvoiceId]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const response = await billingService.createInvoice(formData);
    if (response.success) {
      setShowModal(false);
      resetForm();
      loadInvoices();
    }
  };

  const handleFinalize = async () => {
    if (!finalizing) return;
    setFinalizeBusy(true);
    const response = await billingService.finalizeInvoice(finalizing.id);
    setFinalizeBusy(false);
    setFinalizing(null);
    setNotice(response.success
      ? { ok: true, text: `${finalizing.invoiceNumber} finalized: it is now pending payment` }
      : { ok: false, text: response.error || 'Failed to finalize invoice' });
    if (response.success) loadInvoices();
  };

  const resetForm = () => {
    setFormData({
      organizationId: '',
      invoiceDate: istToday(),
      dueDate: addDays(istToday(), 30),
      items: [],
    });
  };

  /** In the invoice's own currency: every live account is INR. */
  const formatCurrency = (value?: number, currency?: string) => formatMoney(value ?? 0, currency || 'INR');

  /** Invoice, due and period dates are calendar days: never shifted by the browser's zone. */
  const formatDate = formatYmd;

  const getStatusColor = (status: InvoiceStatus) => {
    switch (status) {
      case InvoiceStatus.PAID:
        return 'bg-green-100 text-green-800';
      case InvoiceStatus.PENDING:
        return 'bg-yellow-100 text-yellow-800';
      case InvoiceStatus.OVERDUE:
        return 'bg-red-100 text-red-800';
      case InvoiceStatus.PARTIALLY_PAID:
        return 'bg-blue-100 text-blue-800';
      case InvoiceStatus.DRAFT:
        return 'bg-gray-100 text-gray-800';
      case InvoiceStatus.CANCELLED:
        return 'bg-gray-100 text-gray-600';
      case InvoiceStatus.REFUNDED:
        return 'bg-purple-100 text-purple-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  // Skeleton only before the first answer: once filters are on screen, an empty
  // result must not swap them out mid-edit for a skeleton.
  if (loading && !loadedOnce) {
    return (
      <div className="space-y-6">
        <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">Invoices</h1>
        <SkeletonTable rows={10} columns={7} />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">Invoices</h1>
        <div className="flex flex-wrap gap-2">
        {isBillingAdmin && (
          <button
            onClick={() => setShowUsageModal(true)}
            className="inline-flex items-center px-4 py-2 border border-indigo-600 rounded-md text-sm font-medium text-indigo-600 hover:bg-indigo-50 dark:hover:bg-indigo-900/20"
          >
            <Receipt className="h-4 w-4 mr-2" />
            Invoice usage
          </button>
        )}
        <button
          onClick={() => {
            resetForm();
            setShowModal(true);
          }}
          className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700"
        >
          <Plus className="h-4 w-4 mr-2" />
          New Invoice
        </button>
        </div>
      </div>

      {notice && (
        <div className={`rounded-lg border p-3 text-sm flex justify-between gap-4 ${notice.ok
          ? 'bg-green-50 border-green-200 text-green-800 dark:bg-green-900/20 dark:border-green-800 dark:text-green-300'
          : 'bg-red-50 border-red-200 text-red-800 dark:bg-red-900/20 dark:border-red-800 dark:text-red-300'}`}>
          <span>{notice.text}</span>
          <button onClick={() => setNotice(null)} aria-label="Dismiss">&times;</button>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
        <div className="flex flex-wrap gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Status</label>
            <select
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value as InvoiceStatus | '');
                setPage(1);
              }}
              className="px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-md focus:ring-indigo-500 focus:border-indigo-500"
            >
              <option value="">All Statuses</option>
              {Object.entries(invoiceStatusLabels).map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </select>
          </div>
          <OrgFilterSelect
            value={orgFilter}
            onChange={(v) => {
              setOrgFilter(v);
              setPage(1);
            }}
          />
          <IstDateRange
            label="Invoice date"
            from={dates.from}
            to={dates.to}
            onChange={(r) => {
              setDates(r);
              setPage(1);
            }}
          />
        </div>
      </div>

      {/* Invoices Table */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-700">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Invoice #
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Organization
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Date
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Due Date
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Total
                </th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
              {invoices.map((invoice) => (
                <tr key={invoice.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="text-sm font-medium text-gray-900 dark:text-white">
                      {invoice.invoiceNumber}
                    </span>
                    {isUsageInvoice(invoice) && (
                      <span className="ml-2 inline-flex px-1.5 py-0.5 text-[10px] font-medium rounded bg-indigo-100 text-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-300">
                        USAGE
                      </span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="text-sm text-gray-900 dark:text-white">
                      {invoice.organizationName || invoice.organizationId}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                    {formatDate(invoice.invoiceDate)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                    {formatDate(invoice.dueDate)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right">
                    <div>
                      <span className="text-sm font-medium text-gray-900 dark:text-white">
                        {formatCurrency(invoice.totalAmount, invoice.currency)}
                      </span>
                      {invoice.balanceDue > 0 && invoice.status !== InvoiceStatus.DRAFT && (
                        <div className="text-xs text-red-600">
                          Due: {formatCurrency(invoice.balanceDue, invoice.currency)}
                        </div>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-center">
                    <span
                      className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(
                        invoice.status
                      )}`}
                    >
                      {invoiceStatusLabels[invoice.status]}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right">
                    <div className="flex justify-end gap-2">
                      <button
                        onClick={() => {
                          setSelectedInvoice(invoice);
                          setShowDetailsModal(true);
                        }}
                        className="p-2 text-gray-600 hover:text-gray-800 hover:bg-gray-50 dark:text-gray-400 dark:hover:text-gray-200 dark:hover:bg-gray-700 rounded"
                        title="View Details"
                      >
                        <Eye className="h-4 w-4" />
                      </button>
                      {isBillingAdmin && isPayable(invoice) && (
                        <button
                          onClick={() => setSettling(invoice)}
                          className="p-2 text-indigo-600 hover:text-indigo-800 hover:bg-indigo-50 dark:hover:bg-indigo-900/20 rounded"
                          title="Record payment"
                        >
                          <Banknote className="h-4 w-4" />
                        </button>
                      )}
                      {invoice.status === InvoiceStatus.DRAFT && (
                        <button
                          onClick={() => setFinalizing(invoice)}
                          className="p-2 text-green-600 hover:text-green-800 hover:bg-green-50 dark:hover:bg-green-900/20 rounded"
                          title="Finalize"
                        >
                          <Check className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="bg-white dark:bg-gray-800 px-4 py-3 border-t border-gray-200 dark:border-gray-700 sm:px-6">
          <div className="flex items-center justify-between">
            <p className="text-sm text-gray-700 dark:text-gray-300">
              Showing {invoices.length} of {totalElements} invoices
            </p>
            <div className="flex gap-2">
              <button
                onClick={() => setPage(Math.max(1, page - 1))}
                disabled={page === 1}
                className="px-3 py-1 border border-gray-300 dark:border-gray-600 dark:text-gray-300 rounded text-sm disabled:opacity-50"
              >
                Previous
              </button>
              <button
                onClick={() => setPage(Math.min(totalPages, page + 1))}
                disabled={page >= totalPages}
                className="px-3 py-1 border border-gray-300 dark:border-gray-600 dark:text-gray-300 rounded text-sm disabled:opacity-50"
              >
                Next
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Create Invoice Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <h2 className="text-xl font-semibold mb-4 dark:text-white">Create Invoice</h2>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Organization ID *
                </label>
                <input
                  type="text"
                  required
                  value={formData.organizationId}
                  onChange={(e) =>
                    setFormData({ ...formData, organizationId: e.target.value })
                  }
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Invoice Date *
                  </label>
                  <input
                    type="date"
                    required
                    value={formData.invoiceDate}
                    onChange={(e) =>
                      setFormData({ ...formData, invoiceDate: e.target.value })
                    }
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Due Date *
                  </label>
                  <input
                    type="date"
                    required
                    value={formData.dueDate}
                    onChange={(e) => setFormData({ ...formData, dueDate: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Credits to Purchase
                </label>
                <input
                  type="number"
                  step="0.01"
                  value={formData.creditsPurchased || ''}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      creditsPurchased: parseFloat(e.target.value),
                    })
                  }
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Tax Rate (%)
                </label>
                <input
                  type="number"
                  step="0.01"
                  value={formData.taxRate || ''}
                  onChange={(e) =>
                    setFormData({ ...formData, taxRate: parseFloat(e.target.value) })
                  }
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Notes
                </label>
                <textarea
                  rows={2}
                  value={formData.notes || ''}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>

              <div className="flex justify-end gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowModal(false);
                    resetForm();
                  }}
                  className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="tl-btn tl-btn-primary"
                >
                  Create
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Invoice Details Modal */}
      {showDetailsModal && selectedInvoice && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-start mb-4">
              <div>
                <h2 className="text-xl font-semibold">{selectedInvoice.invoiceNumber}</h2>
                <span
                  className={`inline-flex mt-1 px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(
                    selectedInvoice.status
                  )}`}
                >
                  {invoiceStatusLabels[selectedInvoice.status]}
                </span>
              </div>
              <button
                onClick={() => setShowDetailsModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                &times;
              </button>
            </div>

            <div className="grid grid-cols-2 gap-4 mb-6">
              <div>
                <p className="text-sm text-gray-500">Organization</p>
                <p className="font-medium">
                  {selectedInvoice.organizationName || selectedInvoice.organizationId}
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Invoice Date</p>
                <p className="font-medium">{formatDate(selectedInvoice.invoiceDate)}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Due Date</p>
                <p className="font-medium">{formatDate(selectedInvoice.dueDate)}</p>
              </div>
              {(selectedInvoice.billingPeriodStart || selectedInvoice.billingPeriodEnd) && (
                <div>
                  <p className="text-sm text-gray-500">Billing Period</p>
                  <p className="font-medium">
                    {formatDate(selectedInvoice.billingPeriodStart)} – {formatDate(selectedInvoice.billingPeriodEnd)}
                  </p>
                </div>
              )}
              {!!selectedInvoice.creditsPurchased && (
                <div>
                  <p className="text-sm text-gray-500">Credits Purchased</p>
                  <p className="font-medium">{formatCurrency(selectedInvoice.creditsPurchased, selectedInvoice.currency)}</p>
                </div>
              )}
            </div>

            {selectedInvoice.items && selectedInvoice.items.length > 0 && (
              <div className="border-t border-gray-200 pt-4 mb-4">
                <h3 className="font-medium mb-2">Line items</h3>
                <div className="overflow-x-auto">
                  <table className="min-w-full text-sm">
                    <thead>
                      <tr className="text-xs text-gray-500 uppercase border-b border-gray-200">
                        <th className="text-left py-1 pr-3">Description</th>
                        <th className="text-right py-1 px-3">{isUsageInvoice(selectedInvoice) ? 'Minutes' : 'Qty'}</th>
                        <th className="text-right py-1 px-3">Rate</th>
                        <th className="text-right py-1 pl-3">Amount</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {selectedInvoice.items.map((item) => (
                        <tr key={item.id}>
                          <td className="py-1 pr-3">{item.description}</td>
                          <td className="py-1 px-3 text-right">{item.quantity}</td>
                          <td className="py-1 px-3 text-right">{formatMoney(item.unitPrice, selectedInvoice.currency, 2)}</td>
                          <td className="py-1 pl-3 text-right">{formatCurrency(item.totalPrice, selectedInvoice.currency)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            <div className="border-t border-gray-200 pt-4 mb-4">
              <h3 className="font-medium mb-2">Summary</h3>
              <div className="space-y-1 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-500">Subtotal</span>
                  <span>{formatCurrency(selectedInvoice.subtotal, selectedInvoice.currency)}</span>
                </div>
                {selectedInvoice.taxAmount > 0 && (
                  <div className="flex justify-between">
                    <span className="text-gray-500">Tax ({selectedInvoice.taxRate}%)</span>
                    <span>{formatCurrency(selectedInvoice.taxAmount, selectedInvoice.currency)}</span>
                  </div>
                )}
                {selectedInvoice.discountAmount > 0 && (
                  <div className="flex justify-between text-green-600">
                    <span>Discount</span>
                    <span>-{formatCurrency(selectedInvoice.discountAmount, selectedInvoice.currency)}</span>
                  </div>
                )}
                <div className="flex justify-between font-semibold text-base pt-2 border-t border-gray-200">
                  <span>Total</span>
                  <span>{formatCurrency(selectedInvoice.totalAmount, selectedInvoice.currency)}</span>
                </div>
                {selectedInvoice.amountPaid > 0 && (
                  <div className="flex justify-between text-green-600">
                    <span>Paid</span>
                    <span>-{formatCurrency(selectedInvoice.amountPaid, selectedInvoice.currency)}</span>
                  </div>
                )}
                {selectedInvoice.balanceDue > 0 && (
                  <div className="flex justify-between font-semibold text-red-600 pt-2 border-t border-gray-200">
                    <span>Balance Due</span>
                    <span>{formatCurrency(selectedInvoice.balanceDue, selectedInvoice.currency)}</span>
                  </div>
                )}
              </div>
            </div>

            {selectedInvoice.notes && (
              <div className="border-t border-gray-200 pt-4">
                <h3 className="font-medium mb-2">Notes</h3>
                <p className="text-sm text-gray-600">{selectedInvoice.notes}</p>
              </div>
            )}

            <div className="flex justify-end pt-4">
              <button
                onClick={() => setShowDetailsModal(false)}
                className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      <ConfirmDialog
        isOpen={!!finalizing}
        onClose={() => setFinalizing(null)}
        onConfirm={handleFinalize}
        isLoading={finalizeBusy}
        variant="warning"
        title={`Finalize ${finalizing?.invoiceNumber || 'invoice'}?`}
        message={`It moves from DRAFT to PENDING and can no longer be edited. Total ${formatCurrency(finalizing?.totalAmount, finalizing?.currency)} for ${finalizing?.organizationName || finalizing?.organizationId || ''}. Check it against the voice statement first.`}
        confirmText="Finalize"
      />

      {showUsageModal && (
        <InvoiceUsageModal
          onClose={() => setShowUsageModal(false)}
          onDone={(text, created) => {
            setShowUsageModal(false);
            setNotice({ ok: true, text });
            if (created) {
              // Show the new draft: clear the filters that could hide it. Changing them
              // re-reads the list; the reload covers the case where nothing changed.
              setStatusFilter('');
              setOrgFilter('');
              setDates({ from: '', to: '' });
              setPage(1);
              loadInvoices();
            }
          }}
        />
      )}

      {settling && (
        <SettleInvoiceModal
          invoice={settling}
          onClose={() => setSettling(null)}
          onDone={(text) => {
            setSettling(null);
            setNotice({ ok: true, text });
            loadInvoices();
          }}
        />
      )}
    </div>
  );
};

export default Invoices;
